import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, UserPlus, Send, Eye, Gift, Check } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { feedback, fmtDT } from "@/lib/qr";
import { Coin, VipBadge } from "@/lib/points";
import { countryFlag } from "@/lib/places";
import { useConfirm } from "@/context/ConfirmContext";
import { OfferCard } from "@/components/qr/ListingOffers";

export function SendPoints({ name, myPoints, initial, submit, onSent }) {
  const { confirm } = useConfirm();
  const [pts, setPts] = useState(initial?.pts || 10);
  const [msg, setMsg] = useState(initial?.msg || "");
  const send = async () => {
    const n = parseInt(pts, 10);
    if (!n || n <= 0) return toast.error("Zgjidh sa kredite");
    if (!(await confirm({ title: "Konfirmo dërgimin", message: `Do t'i dërgosh ${n} kredite te ${name}. Vazhdo?` }))) return;
    try { const { data } = await submit(n, msg); feedback(true); onSent(data); }
    catch (e) { feedback(false); toast.error(errMsg(e)); }
  };
  return (
    <div className="bg-slate-50 rounded-2xl p-3 space-y-2" data-testid="qr-send-points">
      <div className="text-sm font-semibold text-slate-800">Sa kredite dëshiron t'i dërgosh? {myPoints != null && <span className="text-xs text-slate-400">(ke {myPoints})</span>}</div>
      <AmountPicker pts={pts} setPts={setPts} msg={msg} setMsg={setMsg} />
      {myPoints != null && Number(pts) > Number(myPoints)
        ? <>
          <div data-testid="qr-pts-short" className="rounded-xl bg-rose-50 border border-rose-200 p-2.5 text-sm font-semibold text-rose-700">🔴 Nuk keni GjejKredit të mjaftueshme. Ju duhen edhe {Math.round((Number(pts) - Number(myPoints)) * 100) / 100} kredite.</div>
          <button data-testid="qr-pts-buy" onClick={() => { window.location.href = "/gjejkredit/bli"; }} className="w-full h-11 rounded-xl bg-amber-500 text-white font-bold">Bli GjejKredit</button>
        </>
        : <button data-testid="qr-pts-send" onClick={send} className="w-full h-11 rounded-xl bg-emerald-500 text-white font-bold">Dërgo {parseInt(pts, 10) || ""} kredite te {name}</button>}
    </div>
  );
}

export function AmountPicker({ pts, setPts, msg, setMsg }) {
  return (<>
    <div className="grid grid-cols-4 gap-2">{[5, 10, 20, 50].map((v) => <button key={v} type="button" data-testid={`qr-pts-${v}`} onClick={() => setPts(v)} className={`h-11 rounded-xl font-bold border ${Number(pts) === v ? "bg-emerald-50 border-emerald-400 text-emerald-700" : "bg-white border-slate-200"}`}>{v}</button>)}</div>
    <input data-testid="qr-pts-custom" type="number" min="1" value={pts} onChange={(e) => setPts(e.target.value)} className="w-full h-10 rounded-xl border border-slate-200 px-3 text-sm" />
    <input data-testid="qr-pts-msg" value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Mesazh (opsional), p.sh. Për një kafe" className="w-full h-10 rounded-xl border border-slate-200 px-3 text-sm" />
  </>);
}

function UserResult({ r, preset }) {
  const nav = useNavigate();
  const [rel, setRel] = useState(r.relationship);
  const [send, setSend] = useState(!!preset && r.can_transfer);
  const [sent, setSent] = useState(null);
  const u = r.user;
  const friend = async () => { try { const { data } = await api.post(`/friends/request/${u.id}`); setRel(data.status); toast.success("Kërkesa u dërgua"); } catch (e) { toast.error(errMsg(e)); } };
  const relText = { friends: "Jeni shokë", pending_out: "Kërkesa u dërgua", pending_in: "Të ka dërguar kërkesë" }[rel];
  return (
    <div className="bg-white rounded-3xl p-5 border border-slate-100 space-y-3" data-testid="qr-result-user">
      <div className="text-center">
        <div className="w-20 h-20 mx-auto rounded-full overflow-hidden bg-emerald-500 text-white text-3xl font-bold flex items-center justify-center ring-4 ring-emerald-100">{u.avatar ? <img src={u.avatar} alt="" className="w-full h-full object-cover" /> : u.name?.[0]?.toUpperCase()}</div>
        <div className="flex items-center justify-center gap-1 mt-2"><span className="font-extrabold text-lg" translate="no" data-testid="qr-result-name">{u.name}</span>{u.vip && <VipBadge testid="qr-result-vip" />}</div>
        <div className="text-sm text-slate-500">{countryFlag(u.country)} {u.city}{u.country ? `, ${u.country}` : ""}</div>
        <div className="inline-flex items-center gap-1 text-xs font-bold text-slate-600 mt-1"><Coin size={14} />{u.points} kredite</div>
      </div>
      {sent ? (
        <div className="bg-emerald-50 rounded-2xl p-4 text-center" data-testid="qr-sent-ok"><CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" /><div className="font-bold mt-1">{sent.points} kredite u dërguan te {sent.to}</div><div className="text-[11px] text-slate-500">ID {sent.tx_id}</div></div>
      ) : (<>
        {relText ? <div className="h-12 rounded-xl bg-emerald-50 text-emerald-700 font-bold flex items-center justify-center gap-2" data-testid="qr-rel"><Check className="w-5 h-5" />{relText}</div>
          : <button data-testid="qr-friend" onClick={friend} className="w-full h-12 rounded-xl bg-emerald-500 text-white font-bold flex items-center gap-3 px-4"><UserPlus className="w-5 h-5" />Bëhu shok</button>}
        {r.can_transfer && <button data-testid="qr-send-toggle" onClick={() => setSend((x) => !x)} className="w-full h-12 rounded-xl bg-slate-100 text-slate-800 font-bold flex items-center gap-3 px-4"><Send className="w-5 h-5" />Dërgo GjejKredit</button>}
        {send && <SendPoints name={u.name} myPoints={r.my_points} initial={preset} submit={(n, m) => api.post("/qr/transfer", { code: r.code, points: n, message: m })} onSent={setSent} />}
      </>)}
      <button data-testid="qr-view-profile" onClick={() => nav(`/perdorues/${u.id}`)} className="w-full h-12 rounded-xl bg-slate-100 text-slate-800 font-bold flex items-center gap-3 px-4"><Eye className="w-5 h-5" />Shiko profilin</button>
    </div>
  );
}

function Success({ title, children, testid }) {
  return (
    <div className="bg-white rounded-3xl p-6 border border-slate-100 text-center space-y-3" data-testid={testid}>
      <CheckCircle2 className="w-20 h-20 text-emerald-500 mx-auto" />
      <div className="text-2xl font-black text-slate-900">{title}</div>
      {children}
    </div>
  );
}

export function ScanResult({ r, preset }) {
  const nav = useNavigate();
  if (r.type === "user") return <UserResult r={r} preset={preset} />;
  if (r.type === "visit") return (
    <Success title="Vizita u regjistrua!" testid="qr-result-visit">
      {r.points > 0 && <div className="text-2xl font-black text-emerald-600" data-testid="qr-visit-points">+{r.points} kredite</div>}
      <div className="bg-slate-50 rounded-2xl p-3"><div className="font-bold" translate="no">{r.business.name}</div><div className="text-xs text-slate-500">{fmtDT(r.created_at)} · ID {r.tx_id}</div></div>
      <p className="text-sm text-slate-500">Faleminderit për vizitën!</p>
      {r.offers?.filter((o) => o.visits_needed).map((o) => <div key={o.id} className="text-left"><OfferCard o={o} /></div>)}
      {r.offers?.length > 0 && <button data-testid="qr-visit-offers" onClick={() => nav(`/shpallje/${r.business.listing_id}`)} className="w-full h-12 rounded-xl bg-emerald-500 text-white font-bold flex items-center justify-center gap-2"><Gift className="w-5 h-5" />Shiko ofertat ({r.offers.length})</button>}
    </Success>
  );
  return (
    <Success title="Oferta u përdor!" testid="qr-result-offer">
      <div className="bg-slate-50 rounded-2xl p-3"><div className="font-bold">{r.offer.name}</div><div className="text-sm text-slate-600" translate="no">Klienti: {r.customer.name}</div><div className="text-xs text-slate-500">{fmtDT(r.created_at)} · ID {r.tx_id}</div></div>
      {r.offer.cost > 0 && <div className="text-sm text-slate-500">{r.offer.cost} kredite u zbritën nga klienti</div>}
    </Success>
  );
}
